import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp, TrendingDown, Activity, Gauge, BarChart3 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface MarketData {
  btcPrice: number;
  btcChange24h: number;
  ethPrice: number;
  ethChange24h: number;
  fearGreedIndex: number;
  fearGreedLabel: string;
  totalMarketCap: number;
  btcDominance: number;
}

export const MarketBriefingBar = () => {
  const [data, setData] = useState<MarketData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        // Fetch market data via edge function (avoids CORS, uses API key)
        const { data: marketData, error } = await supabase.functions.invoke('fetch-market-overview');

        if (error) {
          console.error('Edge function error:', error);
          throw error;
        }

        setData({
          btcPrice: marketData.btcPrice ?? 0,
          btcChange24h: marketData.btcChange24h ?? 0,
          ethPrice: marketData.ethPrice ?? 0,
          ethChange24h: marketData.ethChange24h ?? 0,
          fearGreedIndex: marketData.fearGreedIndex ?? 50,
          fearGreedLabel: marketData.fearGreedLabel ?? 'Neutral',
          totalMarketCap: marketData.totalMarketCap ?? 0,
          btcDominance: marketData.btcDominance ?? 0,
        });
      } catch (error) {
        console.error('Failed to fetch market data:', error);
        // Set fallback data
        setData({
          btcPrice: 0,
          btcChange24h: 0,
          ethPrice: 0,
          ethChange24h: 0,
          fearGreedIndex: 50,
          fearGreedLabel: 'Neutral',
          totalMarketCap: 0,
          btcDominance: 0,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchMarketData();
    const interval = setInterval(fetchMarketData, 300000); // Update every 5 minutes to conserve API quota
    return () => clearInterval(interval);
  }, []);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: price < 10 ? 2 : 0,
    }).format(price);
  };

  const formatMarketCap = (cap: number) => {
    if (cap >= 1e12) return `A$${(cap / 1e12).toFixed(2)}T`;
    if (cap >= 1e9) return `A$${(cap / 1e9).toFixed(0)}B`;
    return `A$${(cap / 1e6).toFixed(0)}M`;
  };

  const getFearGreedColor = (value: number) => {
    if (value <= 25) return 'text-destructive';
    if (value <= 45) return 'text-orange';
    if (value <= 55) return 'text-warning';
    if (value <= 75) return 'text-success';
    return 'text-success';
  };

  if (loading) {
    return (
      <section className="container mx-auto px-4 py-3">
        <Card className="bg-card/80 border-border/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-6 overflow-x-auto scrollbar-hide">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-10 w-32 shrink-0" />
              ))}
            </div>
          </CardContent>
        </Card>
      </section>
    );
  }

  if (!data) return null;

  return (
    <section className="container mx-auto px-4 py-3">
      <Card className="bg-card/80 border-border/50 backdrop-blur">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-6 overflow-x-auto scrollbar-hide">
            {/* BTC Price */}
            <div className="flex items-center gap-3 shrink-0">
              <div className="w-8 h-8 rounded-full bg-orange/20 flex items-center justify-center">
                <span className="text-orange font-bold text-sm">₿</span>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Bitcoin</div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-foreground">{formatPrice(data.btcPrice)}</span>
                  <Badge
                    variant="outline"
                    className={`text-xs gap-1 ${
                      data.btcChange24h >= 0
                        ? 'text-success border-success/30'
                        : 'text-destructive border-destructive/30'
                    }`}
                  >
                    {data.btcChange24h >= 0 ? (
                      <TrendingUp className="w-3 h-3" />
                    ) : (
                      <TrendingDown className="w-3 h-3" />
                    )}
                    {Math.abs(data.btcChange24h).toFixed(1)}%
                  </Badge>
                </div>
              </div>
            </div>

            <div className="w-px h-10 bg-border/50 shrink-0" />

            {/* ETH Price */}
            <div className="flex items-center gap-3 shrink-0">
              <div className="w-8 h-8 rounded-full bg-purple/20 flex items-center justify-center">
                <span className="text-purple font-bold text-sm">Ξ</span>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Ethereum</div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-foreground">{formatPrice(data.ethPrice)}</span>
                  <Badge
                    variant="outline"
                    className={`text-xs gap-1 ${
                      data.ethChange24h >= 0
                        ? 'text-success border-success/30'
                        : 'text-destructive border-destructive/30'
                    }`}
                  >
                    {data.ethChange24h >= 0 ? (
                      <TrendingUp className="w-3 h-3" />
                    ) : (
                      <TrendingDown className="w-3 h-3" />
                    )}
                    {Math.abs(data.ethChange24h).toFixed(1)}%
                  </Badge>
                </div>
              </div>
            </div>

            <div className="w-px h-10 bg-border/50 shrink-0" />

            {/* Fear & Greed */}
            <div className="flex items-center gap-3 shrink-0">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <Gauge className="w-4 h-4 text-primary" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Fear & Greed</div>
                <div className="flex items-center gap-2">
                  <span className={`font-bold ${getFearGreedColor(data.fearGreedIndex)}`}>
                    {data.fearGreedIndex}
                  </span>
                  <span className="text-sm text-muted-foreground">{data.fearGreedLabel}</span>
                </div>
              </div>
            </div>

            <div className="w-px h-10 bg-border/50 shrink-0" />

            {/* Total Market Cap */}
            <div className="flex items-center gap-3 shrink-0">
              <div className="w-8 h-8 rounded-full bg-success/20 flex items-center justify-center">
                <BarChart3 className="w-4 h-4 text-success" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Total Market Cap</div>
                <span className="font-bold text-foreground">{formatMarketCap(data.totalMarketCap)}</span>
              </div>
            </div>

            <div className="w-px h-10 bg-border/50 shrink-0" />

            {/* BTC Dominance */}
            <div className="flex items-center gap-3 shrink-0">
              <div className="w-8 h-8 rounded-full bg-warning/20 flex items-center justify-center">
                <Activity className="w-4 h-4 text-warning" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">BTC Dominance</div>
                <span className="font-bold text-foreground">{data.btcDominance.toFixed(1)}%</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
};
